import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { Select } from "../ui/Select";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { completionsApi } from "../../api/completions";
import { useActivities } from "../../hooks/useActivities";
import type { Completion } from "../../types";

const schema = z.object({
  activityId: z.coerce.number().min(1, "Select an activity"),
  achievementCreditId: z.coerce.number().min(1, "Select an achievement credit"),
  status: z.enum(["completed", "pending", "excused"]),
  completedDate: z.string().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  camperId: number;
  /** Pre-select an activity (e.g. when opening from ActivityProgress) */
  initialActivityId?: number;
  onSuccess: () => void;
}

export default function CompletionForm({ camperId, initialActivityId, onSuccess }: Props) {
  const { activities, loading } = useActivities();
  const [submitting, setSubmitting] = useState(false);
  const [existingCompletions, setExistingCompletions] = useState<Completion[]>([]);

  useEffect(() => {
    completionsApi.list({ camperId }).then(setExistingCompletions).catch(() => {});
  }, [camperId]);

  const existingByAcId = useMemo(
    () => new Map(existingCompletions.map((c) => [c.achievementCreditId, c])),
    [existingCompletions]
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      activityId: (initialActivityId ?? "") as unknown as number,
      achievementCreditId: 0,
      status: "completed",
    },
  });

  const status = watch("status");
  const selectedActivityId = watch("activityId");

  const selectedActivity = activities.find((a) => a.id === Number(selectedActivityId));

  const creditOptions = selectedActivity
    ? (selectedActivity.achievementCredits ?? [])
        .filter((ac) => ac.type !== "graduation")
        .map((ac) => {
          const alreadyEarned = existingByAcId.has(ac.id);
          return {
            value: ac.id,
            label: alreadyEarned
              ? `${ac.displayNumber}. ${ac.name} (earned)`
              : `${ac.displayNumber}. ${ac.name}`,
            disabled: alreadyEarned,
          };
        })
    : [];

  const earnedForActivity = useMemo(() => {
    if (!selectedActivity) return [];
    const acIds = new Set((selectedActivity.achievementCredits ?? []).map((ac) => ac.id));
    return existingCompletions.filter((c) => acIds.has(c.achievementCreditId));
  }, [selectedActivity, existingCompletions]);

  // Destructure register so we can chain onChange for activity selection
  const { onChange: onActivityRhfChange, ...activityFieldProps } = register("activityId");

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      await completionsApi.create({
        camperId,
        achievementCreditId: data.achievementCreditId,
        status: data.status,
        completedDate: data.status === "completed" ? data.completedDate : undefined,
        notes: data.notes,
      });
      toast.success("AC recorded!");
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record AC");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Step 1: pick activity */}
      <Select
        label="Activity"
        options={activities.map((a) => ({ value: a.id, label: a.name }))}
        placeholder={loading ? "Loading…" : "Select an activity"}
        error={errors.activityId?.message}
        {...activityFieldProps}
        onChange={(e) => {
          onActivityRhfChange(e);
          setValue("achievementCreditId", 0);
        }}
      />

      {/* Step 2: pick AC within that activity */}
      <Select
        label="Achievement Credits"
        options={creditOptions}
        placeholder={
          !selectedActivityId || Number(selectedActivityId) === 0
            ? "Select an activity first"
            : creditOptions.length === 0
            ? "No credits for this activity"
            : "Select a credit"
        }
        disabled={!selectedActivity || creditOptions.length === 0}
        error={errors.achievementCreditId?.message}
        {...register("achievementCreditId")}
      />

      {earnedForActivity.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm">
          <p className="font-medium text-amber-800 mb-1.5">Already earned in this activity:</p>
          <ul className="space-y-1.5">
            {earnedForActivity.map((completion) => {
              const ac = selectedActivity!.achievementCredits?.find(
                (a) => a.id === completion.achievementCreditId
              );
              return (
                <li key={completion.id} className="text-amber-700">
                  <span className="font-medium">
                    {ac?.displayNumber}. {ac?.name}
                  </span>
                  {" — "}
                  <span className="capitalize">{completion.status}</span>
                  {completion.completedDate && ` on ${completion.completedDate}`}
                  {completion.notes && (
                    <span className="block text-xs text-amber-600 mt-0.5">
                      Notes: {completion.notes}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <Select
        label="Status"
        options={[
          { value: "completed", label: "Completed" },
          { value: "pending", label: "Pending" },
          { value: "excused", label: "Excused" },
        ]}
        error={errors.status?.message}
        {...register("status")}
      />

      {status === "completed" && (
        <Input
          label="Date Completed"
          type="date"
          error={errors.completedDate?.message}
          {...register("completedDate")}
        />
      )}

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          Notes (optional)
        </label>
        <textarea
          {...register("notes")}
          rows={3}
          placeholder="Any relevant notes…"
          className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="submit" variant="primary" loading={submitting}>
          Save AC
        </Button>
      </div>
    </form>
  );
}
